import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { api } from '../utils/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card } from '../components/ui/card';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { ArrowLeft, Sparkles, Upload, ExternalLink, TrendingUp, FileText, DollarSign, Trash2, RefreshCw, ScanLine } from 'lucide-react';
import { toast } from 'sonner';

const formatSGD = (cents) => `S$${(Number(cents || 0) / 100).toFixed(2)}`;

const STATUS_STYLE = {
  pending:  'bg-amber-100 text-amber-800 border-amber-200',
  approved: 'bg-emerald-100 text-emerald-800 border-emerald-200',
  rejected: 'bg-rose-100 text-rose-800 border-rose-200',
};

const TutorDashboardPage = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [profile, setProfile] = useState(null);
  const [stripeStatus, setStripeStatus] = useState(null);
  const [worksheets, setWorksheets] = useState([]);
  const [sales, setSales] = useState({ sales: [], totals: null });
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [onboardingBusy, setOnboardingBusy] = useState(false);
  const [activeTab, setActiveTab] = useState('worksheets');
  const [worksheetMarkerEnabled, setWorksheetMarkerEnabled] = useState(true);

  // Upload form
  const [form, setForm] = useState({
    title: '', description: '', level: 'P4',
    price_cents: 500, num_questions: 10, answer_letters: '',
    pdf: null, preview_pdf: null,
  });

  const refreshStripe = useCallback(async () => {
    try {
      const s = await api.tutorStripeStatus();
      setStripeStatus(s);
    } catch (e) {
      // ignore transient
    }
  }, []);

  const refreshAll = useCallback(async () => {
    try {
      const [me, ws, sl] = await Promise.all([
        api.tutorMe(),
        api.tutorMyWorksheets(),
        api.tutorSales(),
      ]);
      setProfile(me.profile);
      setWorksheets(ws.worksheets || []);
      setSales(sl);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load tutor dashboard');
    }
  }, []);

  useEffect(() => {
    if (!user) return;
    if (user.role !== 'tutor' && user.role !== 'admin') {
      navigate('/tutor/register');
      return;
    }
    (async () => {
      setLoading(true);
      await refreshAll();
      await refreshStripe();
      
      // Check if worksheet marker is enabled
      try {
        const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/worksheet-marker/status`);
        const data = await response.json();
        setWorksheetMarkerEnabled(data.enabled);
      } catch (error) {
        console.error('Failed to check worksheet marker status:', error);
      }
      
      setLoading(false);
    })();
  }, [user, navigate, refreshAll, refreshStripe]);

  useEffect(() => {
    if (searchParams.get('stripe') === 'done') {
      toast.success('Stripe onboarding complete! Verifying…');
      refreshStripe();
    }
  }, [searchParams, refreshStripe]);

  const startOnboarding = async () => {
    setOnboardingBusy(true);
    try {
      const { onboarding_url } = await api.tutorStripeOnboard(window.location.origin);
      window.location.href = onboarding_url;
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to open Stripe onboarding');
      setOnboardingBusy(false);
    }
  };

  const openStripeDashboard = async () => {
    try {
      const { login_url } = await api.tutorStripeDashboardLink();
      window.open(login_url, '_blank', 'noopener');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to open Stripe dashboard');
    }
  };

  const submitUpload = async (e) => {
    e.preventDefault();
    if (!form.pdf) return toast.error('Please attach a worksheet PDF');
    if (form.price_cents < 100) return toast.error('Minimum price is S$1.00');
    const lettersClean = form.answer_letters.replace(/[^A-Da-d]/g, '').toUpperCase();
    if (lettersClean.length !== Number(form.num_questions)) {
      return toast.error(`Answer key must have exactly ${form.num_questions} A-D letters (got ${lettersClean.length})`);
    }
    setUploading(true);
    try {
      await api.tutorUploadWorksheet({
        title: form.title,
        description: form.description,
        level: form.level,
        price_cents: Number(form.price_cents),
        num_questions: Number(form.num_questions),
        answer_letters: lettersClean,
        pdf: form.pdf,
        preview_pdf: form.preview_pdf,
      });
      toast.success('Worksheet submitted for admin review!');
      setForm({ title: '', description: '', level: 'P4', price_cents: 500, num_questions: 10, answer_letters: '', pdf: null, preview_pdf: null });
      setActiveTab('worksheets');
      await refreshAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Upload failed');
    } finally {
      setUploading(false);
    }
  };

  const deleteWorksheet = async (id) => {
    if (!window.confirm('Delete this worksheet?')) return;
    try {
      await api.tutorDeleteWorksheet(id);
      toast.success('Deleted');
      await refreshAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to delete');
    }
  };

  const readyForSales = stripeStatus?.onboarded && stripeStatus?.charges_enabled;

  if (loading) {
    return <div className="min-h-screen flex items-center justify-center text-slate-500">Loading tutor dashboard…</div>;
  }

  return (
    <div className="min-h-screen bg-slate-50 pb-16">
      <header className="bg-white border-b border-slate-200 sticky top-0 z-20">
        <div className="max-w-6xl mx-auto px-4 py-3 flex items-center justify-between">
          <button onClick={() => navigate('/')} className="inline-flex items-center gap-2 text-slate-700 hover:text-slate-900" data-testid="tutor-back-home">
            <ArrowLeft className="w-4 h-4" /> Home
          </button>
          <div className="flex items-center gap-2 text-amber-700">
            <Sparkles className="w-4 h-4" />
            <span className="text-sm font-semibold">Tutors' Treasure Box</span>
          </div>
          <div className="flex items-center gap-3">
            <span className="hidden sm:inline text-sm text-slate-600">{profile?.display_name || user?.name}</span>
            <Button size="sm" variant="ghost" onClick={logout} data-testid="tutor-logout">Log out</Button>
          </div>
        </div>
      </header>

      <div className="max-w-6xl mx-auto px-4 pt-8 space-y-6">
        {/* Stripe onboarding banner */}
        <Card className="p-5 rounded-2xl border-2" data-testid="tutor-stripe-card">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div>
              <div className="flex items-center gap-2 mb-1">
                <DollarSign className="w-5 h-5 text-emerald-600" />
                <h3 className="font-semibold text-slate-900">Payout account</h3>
                {readyForSales
                  ? <Badge className="bg-emerald-100 text-emerald-800 border-emerald-200">Ready to sell</Badge>
                  : <Badge className="bg-amber-100 text-amber-800 border-amber-200">Setup required</Badge>}
              </div>
              <p className="text-sm text-slate-600">
                {readyForSales
                  ? 'Your Stripe account is verified. Buyers can now purchase your worksheets — you keep 95%.'
                  : 'Complete Stripe onboarding (takes ~3 min) to start receiving payouts. Platform fee: 5%.'}
              </p>
            </div>
            <div className="flex gap-2">
              {!readyForSales && (
                <Button onClick={startOnboarding} disabled={onboardingBusy} className="bg-emerald-600 hover:bg-emerald-700" data-testid="tutor-start-onboarding">
                  {onboardingBusy ? 'Opening…' : (stripeStatus?.stripe_account_id ? 'Continue onboarding' : 'Start Stripe onboarding')}
                </Button>
              )}
              {readyForSales && (
                <Button variant="outline" onClick={openStripeDashboard} data-testid="tutor-open-stripe-dashboard">
                  <ExternalLink className="w-4 h-4 mr-2" /> Stripe dashboard
                </Button>
              )}
              <Button variant="ghost" size="sm" onClick={refreshStripe} data-testid="tutor-refresh-stripe" title="Refresh status">
                <RefreshCw className="w-4 h-4" />
              </Button>
            </div>
          </div>
        </Card>

        {/* Sales summary */}
        {sales?.totals && (
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <Card className="p-5 rounded-2xl" data-testid="tutor-stat-sales">
              <div className="text-xs uppercase tracking-wide text-slate-500">Sales</div>
              <div className="text-3xl font-bold text-slate-900 mt-1">{sales.totals.sales_count}</div>
            </Card>
            <Card className="p-5 rounded-2xl" data-testid="tutor-stat-gross">
              <div className="text-xs uppercase tracking-wide text-slate-500">Gross revenue</div>
              <div className="text-3xl font-bold text-slate-900 mt-1">{formatSGD(sales.totals.gross_cents)}</div>
            </Card>
            <Card className="p-5 rounded-2xl" data-testid="tutor-stat-net">
              <div className="text-xs uppercase tracking-wide text-slate-500">You earned (95%)</div>
              <div className="text-3xl font-bold text-emerald-700 mt-1">{formatSGD(sales.totals.net_cents)}</div>
            </Card>
          </div>
        )}

        {/* Tutor Tools — AI Worksheet Marker */}
        {worksheetMarkerEnabled && (
          <Card
            className="p-5 rounded-2xl border-2 border-amber-200 bg-gradient-to-br from-amber-50 to-white"
            data-testid="tutor-ai-marker-card"
          >
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
              <div className="flex items-start gap-3">
                <div className="w-11 h-11 rounded-xl bg-amber-500 text-white flex items-center justify-center flex-shrink-0">
                  <ScanLine className="w-6 h-6" />
                </div>
                <div>
                  <div className="flex items-center gap-2 mb-1">
                    <h3 className="font-semibold text-slate-900">AI Worksheet Marker</h3>
                    <Badge className="bg-emerald-100 text-emerald-800 border-emerald-200">New</Badge>
                  </div>
                  <p className="text-sm text-slate-600">
                    Scan handwritten English worksheets. Prof.Scaly reads the answers, marks them and produces a printable PDF report you can share with parents.
                  </p>
                </div>
              </div>
              <Button
                className="bg-amber-600 hover:bg-amber-700"
                onClick={() => navigate('/tutor/worksheet-marker')}
                data-testid="tutor-open-worksheet-marker"
              >
                Open marker
              </Button>
            </div>
          </Card>
        )}

        {/* Tutor Tools — Chinese Worksheet Marker (PWA) */}
        <Card
          className="p-5 rounded-2xl border-2 border-rose-200 bg-gradient-to-br from-rose-50 to-white"
          data-testid="tutor-chinese-marker-card"
        >
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div className="flex items-start gap-3">
              <div className="w-11 h-11 rounded-xl bg-rose-700 text-white flex items-center justify-center flex-shrink-0 font-serif text-xl font-bold">
                批
              </div>
              <div>
                <div className="flex items-center gap-2 mb-1">
                  <h3 className="font-semibold text-slate-900">中文作业批改助手 · Chinese Worksheet Marker</h3>
                  <Badge className="bg-rose-100 text-rose-800 border-rose-200">New</Badge>
                </div>
                <p className="text-sm text-slate-600">
                  拍照或扫描小学中文作业，GPT-5.2 识别简体字与手写答案，逐题批改并给出中文评语。可安装为手机 App。
                </p>
                <p className="text-xs text-slate-500 mt-1">
                  Mobile install: open in Chrome/Safari → tap the browser menu → <em>Add to Home Screen</em>.
                </p>
              </div>
            </div>
            <Button
              className="bg-rose-700 hover:bg-rose-800 text-white"
              onClick={() => window.open('/chinese-marker/', '_blank', 'noopener,noreferrer')}
              data-testid="tutor-open-chinese-marker"
            >
              Open marker
            </Button>
          </div>
        </Card>

        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList>
            <TabsTrigger value="worksheets" data-testid="tutor-tab-worksheets"><FileText className="w-4 h-4 mr-2" /> My Worksheets</TabsTrigger>
            <TabsTrigger value="upload" data-testid="tutor-tab-upload"><Upload className="w-4 h-4 mr-2" /> Upload New</TabsTrigger>
            <TabsTrigger value="sales" data-testid="tutor-tab-sales"><TrendingUp className="w-4 h-4 mr-2" /> Sales</TabsTrigger>
          </TabsList>

          <TabsContent value="worksheets" className="mt-4 space-y-3">
            <div className="flex items-center justify-between gap-3">
              <p className="text-sm text-slate-600">
                {worksheets.length > 0
                  ? `${worksheets.length} worksheet${worksheets.length === 1 ? '' : 's'} on your shelf.`
                  : 'Ready to publish your first worksheet? Upload a PDF + answer key and we\'ll vet it within 24h.'}
              </p>
              <Button
                onClick={() => setActiveTab('upload')}
                className="bg-amber-600 hover:bg-amber-700"
                data-testid="tutor-upload-new-cta"
              >
                <Upload className="w-4 h-4 mr-2" /> Upload New Worksheet
              </Button>
            </div>
            {worksheets.length === 0 && (
              <Card className="p-10 text-center rounded-2xl border-2 border-dashed border-amber-200 bg-amber-50/40" data-testid="tutor-worksheets-empty">
                <Upload className="w-10 h-10 text-amber-500 mx-auto mb-3" />
                <div className="font-semibold text-slate-900 mb-1">No worksheets yet</div>
                <p className="text-sm text-slate-600 mb-4">
                  Attach a worksheet PDF, set your price, and submit it for review. Approved worksheets go live on the marketplace — you keep 95% of every sale.
                </p>
                <Button
                  onClick={() => setActiveTab('upload')}
                  className="bg-amber-600 hover:bg-amber-700"
                  data-testid="tutor-upload-first-cta"
                >
                  <Upload className="w-4 h-4 mr-2" /> Upload your first worksheet
                </Button>
              </Card>
            )}
            {worksheets.map((w) => (
              <Card key={w.id} className="p-4 rounded-2xl" data-testid={`tutor-worksheet-${w.id}`}>
                <div className="flex flex-col sm:flex-row sm:items-center gap-3 sm:justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <span className={`text-xs font-semibold px-2 py-0.5 rounded-full border ${STATUS_STYLE[w.status] || ''}`}>{w.status}</span>
                      <Badge variant="secondary">{w.level}</Badge>
                      <span className="text-slate-500 text-xs">{w.num_questions} qns</span>
                      {w.has_preview && <Badge className="bg-sky-100 text-sky-800 border-sky-200">Preview attached</Badge>}
                    </div>
                    <div className="font-semibold text-slate-900">{w.title}</div>
                    {w.description && <div className="text-sm text-slate-600 line-clamp-2">{w.description}</div>}
                    {w.status === 'rejected' && w.rejection_reason && (
                      <div className="mt-1 text-sm text-rose-700">Rejected: {w.rejection_reason}</div>
                    )}
                  </div>
                  <div className="text-right">
                    <div className="text-lg font-bold">{formatSGD(w.price_cents)}</div>
                    <div className="text-xs text-slate-500">{w.sales_count || 0} sales</div>
                  </div>
                  <Button size="sm" variant="ghost" className="text-rose-600" onClick={() => deleteWorksheet(w.id)} data-testid={`tutor-delete-${w.id}`}>
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </Card>
            ))}
          </TabsContent>

          <TabsContent value="upload" className="mt-4">
            <Card className="p-6 rounded-2xl">
              <h3 className="font-semibold text-lg mb-4">Upload a new worksheet</h3>
              <form onSubmit={submitUpload} className="space-y-4">
                <div>
                  <Label htmlFor="ws-title">Title</Label>
                  <Input id="ws-title" data-testid="tutor-upload-title" value={form.title}
                    onChange={(e) => setForm({ ...form, title: e.target.value })} required />
                </div>
                <div>
                  <Label htmlFor="ws-desc">Description</Label>
                  <Textarea id="ws-desc" data-testid="tutor-upload-desc" value={form.description}
                    onChange={(e) => setForm({ ...form, description: e.target.value })} rows={3} placeholder="Topics covered, difficulty, etc." />
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                  <div>
                    <Label>Level</Label>
                    <Select value={form.level} onValueChange={(v) => setForm({ ...form, level: v })}>
                      <SelectTrigger data-testid="tutor-upload-level"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="P4">P4</SelectItem>
                        <SelectItem value="P5">P5</SelectItem>
                        <SelectItem value="P6">P6</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label htmlFor="ws-price">Price (SGD)</Label>
                    <Input id="ws-price" data-testid="tutor-upload-price" type="number" min="1" max="100" step="0.5"
                      value={form.price_cents / 100}
                      onChange={(e) => setForm({ ...form, price_cents: Math.round(parseFloat(e.target.value || '0') * 100) })} />
                  </div>
                  <div>
                    <Label htmlFor="ws-nq"># Questions (MCQ)</Label>
                    <Input id="ws-nq" data-testid="tutor-upload-nq" type="number" min="1" max="100"
                      value={form.num_questions}
                      onChange={(e) => setForm({ ...form, num_questions: parseInt(e.target.value || '10', 10) })} />
                  </div>
                </div>
                <div>
                  <Label htmlFor="ws-key">Answer key (A-D letters, one per question)</Label>
                  <Textarea id="ws-key" data-testid="tutor-upload-answers" rows={2}
                    value={form.answer_letters}
                    onChange={(e) => setForm({ ...form, answer_letters: e.target.value.toUpperCase() })}
                    placeholder="e.g. ABCDACBDDC" />
                  <p className="text-xs text-slate-500 mt-1">
                    Non-letters ignored. Buyers submit their answers via an optical bubble sheet and are auto-graded against this key.
                  </p>
                </div>
                <div>
                  <Label htmlFor="ws-pdf">Worksheet PDF</Label>
                  <input id="ws-pdf" data-testid="tutor-upload-pdf" type="file" accept="application/pdf"
                    onChange={(e) => setForm({ ...form, pdf: e.target.files?.[0] || null })}
                    className="block w-full text-sm text-slate-600 file:mr-3 file:px-3 file:py-1.5 file:rounded-md file:border-0 file:bg-amber-600 file:text-white file:cursor-pointer" />
                </div>
                <div>
                  <Label htmlFor="ws-preview">Free preview PDF (optional, ~1 page)</Label>
                  <input id="ws-preview" data-testid="tutor-upload-preview" type="file" accept="application/pdf"
                    onChange={(e) => setForm({ ...form, preview_pdf: e.target.files?.[0] || null })}
                    className="block w-full text-sm text-slate-600 file:mr-3 file:px-3 file:py-1.5 file:rounded-md file:border-0 file:bg-slate-600 file:text-white file:cursor-pointer" />
                  <p className="text-xs text-slate-500 mt-1">Attach a 1-page teaser PDF so buyers can peek before purchasing. Max {5}MB.</p>
                </div>
                <div className="pt-2">
                  <Button type="submit" disabled={uploading} className="bg-amber-600 hover:bg-amber-700" data-testid="tutor-upload-submit">
                    {uploading ? 'Submitting…' : 'Submit for review'}
                  </Button>
                  <p className="text-xs text-slate-500 mt-2">
                    All uploads are reviewed by admin. Once approved, buyers can purchase — you keep 95% (Stripe collects 5% platform fee + normal card fees).
                  </p>
                </div>
              </form>
            </Card>
          </TabsContent>

          <TabsContent value="sales" className="mt-4 space-y-2">
            {(!sales?.sales || sales.sales.length === 0) && (
              <Card className="p-8 text-center text-slate-500 rounded-2xl">No sales yet. Once your worksheet is approved and a buyer purchases, it will appear here.</Card>
            )}
            {sales?.sales?.map((s) => (
              <Card key={s.id || s.stripe_session_id} className="p-3 rounded-2xl flex justify-between items-center" data-testid={`tutor-sale-${s.id || s.stripe_session_id}`}>
                <div>
                  <div className="text-sm text-slate-900">{new Date(s.paid_at || s.created_at).toLocaleString()}</div>
                  <div className="text-xs text-slate-500">Worksheet #{(s.worksheet_id || '').slice(0, 6)} • Buyer {(s.buyer_email || s.buyer_id).slice(0, 24)}</div>
                </div>
                <div className="text-right">
                  <div className="font-semibold text-emerald-700">+{formatSGD(s.tutor_net_cents)}</div>
                  <div className="text-xs text-slate-500">Gross {formatSGD(s.amount_cents)} • Fee {formatSGD(s.application_fee_cents)}</div>
                </div>
              </Card>
            ))}
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};

export default TutorDashboardPage;
